/**
 * Pay Charge Embed - TypeScript Types
 */

/** Integration mode for the payment form */
export type PayChargeMode = 'iframe' | 'redirect';

/** Form layout style for the payment form */
export type PayChargeFormLayout = 'default' | 'compact';

/** Configuration options for PayChargeEmbed */
export interface PayChargeEmbedConfig {
  /** ID of the container element where the iframe will be injected (required for iframe mode) */
  containerId?: string;

  /** JWT session token from the server */
  sessionToken: string;

  /** Integration mode: iframe (embedded) or redirect (full-page checkout). Defaults to 'iframe'. */
  mode?: PayChargeMode;

  /** URL of the iframe content (heartland client). Defaults to relative path. (iframe mode only) */
  iframeUrl?: string;

  /** API base URL. Used for both iframe and redirect modes. Defaults to relative path. */
  apiUrl?: string;

  /** Custom CSS for the iframe element (iframe mode only) */
  iframeCss?: string;

  /** Custom labels passed to the payment form (iframe mode only) */
  labels?: PayChargeLabels;

  /** Form layout: 'default' (stacked) or 'compact' (horizontal, connected borders). Defaults to 'default'. */
  formLayout?: PayChargeFormLayout;
}

/** Customizable labels for the payment form */
export interface PayChargeLabels {
  submitButton?: string;
  cardNumber?: string;
  cardHolder?: string;
  expiration?: string;
  cvv?: string;
  billingZip?: string;
}

/** Options passed to initialize() */
export interface InitializeOptions {
  /** Override integration mode for this initialization call */
  mode?: PayChargeMode;

  /** Payment amount in decimal dollars (#549) - 10.00 is ten dollars */
  amount?: number;

  /** Currency code (default: USD) */
  currency?: string;

  /** Additional metadata */
  metadata?: Record<string, string>;
}

/** Custom property attached to a session/charge for integrator-specific data */
export interface CustomProperty {
  developerId?: string;
  key: string;
  value: string;
}

/** Charge success event detail */
export interface ChargeSuccessDetail {
  chargeId: string;
  amount: number;
  currency: string;
  status: string;
  providerTransactionId?: string;
  providerReferenceNumber?: string;
  externalReference?: { type?: string; id?: string };
  cardBin?: string;
  last4?: string;
  cardType?: string;

  /** Session ID — use to verify state server-side via GET /api/v1/sessions/{id} */
  sessionId?: string;

  /** Descriptor echoed back from session creation */
  chargeDescriptor?: string;

  /** Cardholder name (optional, may not be collected) */
  cardHolderName?: string;

  /** Cardholder postal code (optional, may not be collected) */
  cardHolderPostalCode?: string;

  /** Session metadata echoed back */
  metadata?: Record<string, string>;

  /** Custom properties echoed back from session creation */
  customProperties?: CustomProperty[];
}

/** Charge failed event detail */
export interface ChargeFailedDetail {
  error: {
    code: string;
    message: string;
  };
  canRetry: boolean;
}

/** Field change event detail */
export interface FieldChangeDetail {
  field: string;
  valid: boolean;
  cardType?: string;
}

/** Error event detail */
export interface ErrorDetail {
  code: string;
  message: string;
}

/** Resize event detail */
export interface ResizeDetail {
  height: number;
}

/** Message types for postMessage communication */
export const MESSAGE_TYPES = {
  // Parent -> Iframe
  CONFIGURE: 'pce:configure',
  SET_AMOUNT: 'pce:setAmount',

  // Iframe -> Parent
  READY: 'pce:ready',
  FIELD_UPDATE: 'pce:fieldUpdate',
  RESIZE: 'pce:resize',
  CHARGE_SUCCESS: 'pce:chargeSuccess',
  CHARGE_FAILED: 'pce:chargeFailed',
  ERROR: 'pce:error',
} as const;

/** Structure of messages sent via postMessage */
export interface EmbedMessage<T = unknown> {
  type: string;
  payload: T;
  timestamp: number;
}

/** Options for redirectToCheckout helper function */
export interface RedirectToCheckoutOptions {
  /** JWT session token containing the session ID */
  sessionToken: string;

  /** Base URL for the checkout page. Defaults to current origin. */
  baseUrl?: string;
}
